import { groq } from 'next-sanity';

export interface TimeRange {
    key: string;
    startTime: string | null;
    endTime: string | null;
}

const queryFields =
    "{title,overview,'slug':slug.current,'authors':authors[]->{'name':name,'avatar':image.asset->url},mainImage,'body':body[]{...,_type=='video'=>{'videoUrl':video.asset->url}},publishedAt}";

const order = 'order(publishedAt desc)';

export const POST_QUERY = groq`*[_type == "post" && language == $lang && slug.current == $slug][0]${queryFields}`;

export const buildRssPostsQuery = () => {
    return groq`*[_type in ["news", "research", "weekly"] && language == $lang && defined(slug) && defined(publishedAt)]{
      title,
      'description': coalesce(description, overview, pt::text(richText), pt::text(body)),
      'slug': slug.current,
      'type': _type,
      'imageUrl': coalesce(image.asset->url, mainImage.asset->url),
      publishedAt
    }[$start...$end] | ${order}`;
};

export const BLOG_MENU_POSTS_QUERY = groq`[
    *[_type == "news"     && language == $lang && defined(slug) && defined(publishedAt)] | order(publishedAt desc)[0],
    *[_type == "research" && language == $lang && defined(slug) && defined(publishedAt)] | order(publishedAt desc)[0],
    *[_type == "weekly"   && language == $lang && defined(slug) && defined(publishedAt)] | order(publishedAt desc)[0]
][@ != null]{
    title,
    'description': coalesce(description, overview, pt::text(richText), pt::text(body)),
    'slug': slug.current,
    'type': _type,
    'imageUrl': coalesce(image.asset->url, mainImage.asset->url),
    publishedAt
}`;

export const STRICK_TOPIC_QUERY = groq`*[_type == "post" && language == $lang && defined(slug) && references(*[_type=="category" && title == 'st']._id)] | ${order}[0]${queryFields}`;
export const CATEGORIES_QUERY = groq`*[_type == "category"]`;
export const AUTHOR_QUERY = groq`*[_type == "author"]{name,'slug':slug,image}`;
export const VIDEO_QUERY = groq`*[_type == "video" && defined(slug) && slug == $slug][0]{
    title,'posterUrl':post.asset->url,'slug':slug,'url':dataSrc.asset->url
  }`;

const buildCategoryQuery = (categories: string[] | null) => {
    return categories
        ? `references(*[_type=="category" && title in [${categories
              .filter((category) => category !== '')
              .map((category) => `"${category}"`)
              .join(',')}]]._id)`
        : null;
};

const buildAuthorQuery = (authors: string[] | null) => {
    return authors
        ? `references(*[_type=="author" && slug in [${authors
              .filter((author) => author !== '')
              .map((author) => `"${author}"`)
              .join(',')}]]._id)`
        : null;
};

const buildTimeRangeQuery = (timeRange: TimeRange | null) => {
    if (!timeRange) {
        return '';
    }

    const startTimeQuery = timeRange.startTime
        ? `dateTime(publishedAt) >= dateTime('${timeRange.startTime}')`
        : null;

    const endTimeQuery = timeRange.endTime
        ? `dateTime(publishedAt) <= dateTime('${timeRange.endTime}')`
        : null;

    let timeRangeQuery = '';
    if (startTimeQuery && endTimeQuery) {
        timeRangeQuery = `${startTimeQuery} && ${endTimeQuery}`;
    } else if (startTimeQuery) {
        timeRangeQuery = startTimeQuery;
    } else if (endTimeQuery) {
        timeRangeQuery = endTimeQuery;
    }

    return timeRangeQuery;
};

export const buildPostCountQuery = (
    categories: string[] | null,
    authors: string[] | null,
    timeRange: TimeRange | null,
) => {
    const categoriesQuery = buildCategoryQuery(categories);
    const authorsQuery = buildAuthorQuery(authors);
    const groupQueries = [categoriesQuery, authorsQuery].filter(
        (query) => query != null,
    );
    const timeRangeQuery = buildTimeRangeQuery(timeRange);

    let conditions = '';

    if (groupQueries.length > 0) {
        conditions += ' && (' + groupQueries.join(' || ') + ')';
    }

    if (timeRangeQuery) {
        conditions += ' && ' + timeRangeQuery;
    }

    const query =
        'count(*[_type == "post" && language == $lang && defined(slug)' +
        conditions +
        '])';

    return groq`${query}`;
};

export const buildPostQuery = (
    categories: string[] | null,
    authors: string[] | null,
    timeRange: TimeRange | null,
) => {
    const categoriesQuery = buildCategoryQuery(categories);
    const authorsQuery = buildAuthorQuery(authors);
    const groupQueries = [categoriesQuery, authorsQuery].filter(
        (query) => query != null,
    );
    const timeRangeQuery = buildTimeRangeQuery(timeRange);

    const query = `*[_type == "post" && language == $lang && defined(slug) ${
        groupQueries.length > 0 ? `&& (${groupQueries.join(' || ')})` : ''
    } ${
        timeRangeQuery ? `&& ${timeRangeQuery}` : ''
    }]${queryFields}[$start...$end] | ${order}`;

    return groq`${query}`;
};

export const buildPostSlugQuery = (
    categories: string[] | null,
    authors: string[] | null,
    timeRange: TimeRange | null,
) => {
    const categoriesQuery = buildCategoryQuery(categories);
    const authorsQuery = buildAuthorQuery(authors);
    const groupQueries = [categoriesQuery, authorsQuery].filter(
        (query) => query != null,
    );
    const timeRangeQuery = buildTimeRangeQuery(timeRange);

    const query = `*[_type == "post" && language == $lang && defined(slug) ${
        groupQueries.length > 0 ? `&& (${groupQueries.join(' || ')})` : ''
    } ${timeRangeQuery ? `&& ${timeRangeQuery}` : ''}]{slug}[$start...$end]`;

    // console.log('buildPostQuery', groq`${query}`);
    return groq`${query}`;
};
